import React, { useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { getCities as onGetCities } from "store/actions"
import {
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Table,
} from "reactstrap"
import Select from "react-select"
import { toast, ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { post, put } from "helpers/api_helper"

const fromApi = doc => ({
  id: doc._id,
  name: doc.name || "",
  code: doc.code || "",
  status: doc.status !== false,
  description: doc.description || "",
  image: doc.image || "",
  price: doc.price ?? "",
  displayPrice: doc.displayPrice ?? "",
  termValidity: doc.validity ?? "",
  turns: doc.turns ?? "",
  priceFrom: doc.orderRange?.from ?? "",
  priceTo: doc.orderRange?.to ?? "",
  noLimit: doc.orderRange?.to == null,
  allCities: !!doc.enableAllCities,
  cities: (doc.cities || []).map(c => (c && c._id ? c._id : c)),
})

const toApi = form => ({
  name: form.name.trim(),
  code: form.code.trim(),
  status: form.status,
  description: form.description,
  image: form.image,
  price: form.price,
  displayPrice: form.displayPrice,
  validity: form.termValidity,
  turns: form.turns,
  orderRange: { from: form.priceFrom, to: form.noLimit ? null : form.priceTo },
  enableAllCities: form.allCities,
  cities: form.allCities ? [] : form.cities,
})

const validatePackage = form => {
  const errors = {}
  const isEmpty = value => String(value ?? "").trim() === ""

  if (isEmpty(form.name)) errors.name = "Name is required"
  if (isEmpty(form.code)) errors.code = "Code is required"

  if (isEmpty(form.price)) errors.price = "Price is required"
  else if (Number(form.price) <= 0) errors.price = "Price must be greater than 0"

  if (
    !isEmpty(form.displayPrice) &&
    !isEmpty(form.price) &&
    Number(form.displayPrice) < Number(form.price)
  ) {
    errors.displayPrice = "Must be greater than or equal to Price"
  }

  if (isEmpty(form.termValidity)) errors.termValidity = "Validity is required"
  else if (Number(form.termValidity) < 1)
    errors.termValidity = "Validity must be at least 1 month"

  if (isEmpty(form.turns)) errors.turns = "Turns is required"
  else if (Number(form.turns) < 1) errors.turns = "Turns must be at least 1"

  if (isEmpty(form.priceFrom)) errors.priceFrom = "Order range from is required"
  else if (Number(form.priceFrom) < 0)
    errors.priceFrom = "Order range from cannot be negative"

  if (!form.noLimit) {
    if (isEmpty(form.priceTo))
      errors.priceTo = "Order range to is required, or turn on No limit"
    else if (!isEmpty(form.priceFrom) && Number(form.priceTo) < Number(form.priceFrom))
      errors.priceTo = "Must be greater than or equal to From"
  }

  if (!form.allCities && form.cities.length === 0)
    errors.cities = "Select at least one city or turn on All Cities"

  return errors
}

const errorMessage = (err, fallback) =>
  err?.response?.data?.msg || err?.response?.data?.error || fallback

const emptyPackage = {
  name: "",
  displayPrice: "",
  price: "",
  code: "",
  status: true,
  priceFrom: "",
  priceTo: "",
  noLimit: false,
  termValidity: "",
  turns: "",
  image: "",
  description: "",
  cities: [],
  allCities: false,
}

function PackageImage({ src, size = 40 }) {
  if (!src) {
    return (
      <div
        className="avatar-title rounded bg-light text-muted"
        style={{ width: size, height: size }}
      >
        <i className="bx bx-image font-size-18" />
      </div>
    )
  }
  return (
    <img
      src={src}
      alt=""
      className="rounded border"
      style={{ width: size, height: size, objectFit: "contain" }}
    />
  )
}

function Package() {
  document.title = "Subscription >> Packages"

  const [modal, setModal] = useState(false)
  const [packages, setPackages] = useState([])
  const [form, setForm] = useState(emptyPackage)
  const [editId, setEditId] = useState(null)

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)

  const dispatch = useDispatch()
  const cities = useSelector(state => state.City.cities) || []

  useEffect(() => {
    dispatch(onGetCities())
  }, [dispatch])

  const [search, setSearch] = useState("")
  const [showEnabled, setShowEnabled] = useState(true)
  const [showDisabled, setShowDisabled] = useState(true)
  const latestRequest = useRef(0)

  const fetchPackages = async ({ search, showEnabled, showDisabled }) => {
    const requestId = ++latestRequest.current

    if (!showEnabled && !showDisabled) {
      setPackages([])
      setLoading(false)
      return
    }

    const filters = {}
    if (search.trim()) filters.search = search.trim()
    if (showEnabled !== showDisabled) filters.status = showEnabled

    setLoading(true)
    try {
      const data = await post("packages/search", filters)
      if (requestId !== latestRequest.current) return
      setPackages((Array.isArray(data) ? data : []).map(fromApi))
    } catch (err) {
      if (requestId === latestRequest.current) {
        toast.error(errorMessage(err, "Could not load packages"))
      }
    } finally {
      if (requestId === latestRequest.current) setLoading(false)
    }
  }

  useEffect(() => {
    const timer = setTimeout(
      () => fetchPackages({ search, showEnabled, showDisabled }),
      search ? 400 : 0,
    )
    return () => clearTimeout(timer)
  }, [search, showEnabled, showDisabled])

  const cityOptions = cities.map(c => ({ value: c._id, label: c.name }))

  const visiblePackages = packages.filter(
    item => (item.status ? showEnabled : showDisabled),
  )

  const [submitted, setSubmitted] = useState(false)
  const errors = validatePackage(form)

  const fieldError = name => {
    if (submitted) return errors[name]
    if (name === "displayPrice") return errors.displayPrice
    if (name === "priceTo" && form.priceTo !== "") return errors.priceTo
    return undefined
  }

  const openAddModal = () => {
    setEditId(null)
    setForm(emptyPackage)
    setModal(true)
  }

  const openEditModal = item => {
    setEditId(item.id)
    setForm({ ...item })
    setModal(true)
  }

  const closeModal = () => {
    setModal(false)
    setSubmitted(false)
    setEditId(null)
    setForm(emptyPackage)
  }
  const NUMBER_FIELDS = ["price", "displayPrice", "termValidity", "turns", "priceFrom", "priceTo"]

  const handleChange = event => {
    const { name, value } = event.target
    const next = NUMBER_FIELDS.includes(name) ? value.replace(/D/g, "") : value
    setForm(prev => ({ ...prev, [name]: next }))
  }

  const handleImageChange = async event => {
    const file = event.target.files && event.target.files[0]
    event.target.value = ""
    if (!file) return
    if (file.size > 1024 * 1024) {
      toast.error("Image must be 1 MB or smaller")
      return
    }

    const formData = new FormData()
    formData.append("image", file)
    setUploading(true)
    try {
      const response = await post("toys/uploadImage", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      if (response?.path) {
        setForm(prev => ({ ...prev, image: response.path }))
      } else {
        toast.error("Image upload failed")
      }
    } catch (err) {
      toast.error(errorMessage(err, "Image upload failed (JPG or PNG, max 1 MB)"))
    } finally {
      setUploading(false)
    }
  }

  const handleSave = async event => {
    event.preventDefault()
    setSubmitted(true)
    if (Object.keys(errors).length > 0) return

    setSaving(true)
    try {
      const payload = toApi(form)
      const saved = editId
        ? await put(`packages/${editId}`, payload)
        : await post("packages", payload)
      const row = fromApi(saved)

      setPackages(prev =>
        editId
          ? prev.map(item => (item.id === editId ? row : item))
          : [row, ...prev],
      )
      toast.success(editId ? "Package updated" : "Package created")
      closeModal()
    } catch (err) {
      toast.error(errorMessage(err, "Could not save package"))
    } finally {
      setSaving(false)
    }
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Subscription" breadcrumbItem="Packages" />
          <Row>
            <Col xs="12">
              <Card>
                <CardBody>
                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <div className="search-box position-relative">
                        <Input
                          type="text"
                          placeholder="Search name or code..."
                          value={search}
                          onChange={e => setSearch(e.target.value)}
                          style={{ width: 240, paddingLeft: 32 }}
                        />
                        <i
                          className="bx bx-search-alt position-absolute text-muted"
                          style={{
                            left: 10,
                            top: "50%",
                            transform: "translateY(-50%)",
                          }}
                        />
                      </div>
                      <div className="form-check form-switch form-switch-md mb-0 ms-2 d-flex align-items-center">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="filterEnabled"
                          checked={showEnabled}
                          onChange={() => setShowEnabled(prev => !prev)}
                        />
                        <label
                          className="form-check-label font-size-14 ms-1"
                          htmlFor="filterEnabled"
                        >
                          Enabled
                        </label>
                      </div>
                      <div className="form-check form-switch form-switch-md mb-0 ms-2 d-flex align-items-center">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="filterDisabled"
                          checked={showDisabled}
                          onChange={() => setShowDisabled(prev => !prev)}
                        />
                        <label
                          className="form-check-label font-size-14 ms-1"
                          htmlFor="filterDisabled"
                        >
                          Disabled
                        </label>
                      </div>
                    </div>
                    <Button color="success" onClick={openAddModal}>
                      <i className="mdi mdi-plus me-1" />
                      Add Package
                    </Button>
                  </div>
                  <div className="table-responsive">
                    <Table className="table align-middle table-nowrap text-center mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Name</th>
                          <th>Code</th>
                          <th>Price</th>
                          <th>Display Price</th>
                          <th>Order Range</th>
                          <th>Validity</th>
                          <th>Total Turns</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visiblePackages.length === 0 ? (
                          <tr>
                            <td colSpan="9" className="text-center text-muted">
                              {loading
                                ? "Loading packages..."
                                : packages.length === 0
                                  ? "No packages yet"
                                  : "No packages match the filters"}
                            </td>
                          </tr>
                        ) : (
                          visiblePackages.map(item => (
                            <tr key={item.id}>
                              <td>{item.name}</td>
                              <td>{item.code}</td>
                              <td>₹{item.price}</td>
                              <td>
                                {item.displayPrice !== "" ? `₹${item.displayPrice}` : "-"}
                              </td>
                              <td>
                                {item.noLimit
                                  ? `₹${item.priceFrom || 0} & above`
                                  : `₹${item.priceFrom || 0} - ₹${item.priceTo || 0}`}
                              </td>
                              <td>
                                {item.termValidity
                                  ? `${item.termValidity} ${Number(item.termValidity) === 1 ? "month" : "months"}`
                                  : "-"}
                              </td>
                              <td>{item.turns || "-"}</td>
                              <td>
                                <span
                                  className={`badge rounded-pill font-size-12 px-3 py-2 ${
                                    item.status
                                      ? "badge-soft-success"
                                      : "badge-soft-danger"
                                  }`}
                                >
                                  {item.status ? "Active" : "Inactive"}
                                </span>
                              </td>
                              <td>
                                <Link
                                  to="#"
                                  className="text-success"
                                  title="Edit"
                                  onClick={() => openEditModal(item)}
                                >
                                  <i className="mdi mdi-pencil font-size-18" />
                                </Link>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      <Modal isOpen={modal} toggle={closeModal} centered style={{ maxWidth: 680 }}>
        <ModalHeader toggle={closeModal} tag="h4">
          {editId ? "Edit Package" : "Add Package"}
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={handleSave} noValidate>
            <Row>
              <Col md="6" className="mb-3">
                <Label>Name <span className="text-danger">*</span></Label>
                <Input
                  name="name"
                  placeholder="Enter package name"
                  value={form.name}
                  onChange={handleChange}
                  invalid={!!fieldError("name")}
                />
                <FormFeedback>{fieldError("name")}</FormFeedback>
              </Col>
              <Col md="6" className="mb-3">
                <Label>Code <span className="text-danger">*</span></Label>
                <Input
                  name="code"
                  placeholder="Enter package code"
                  value={form.code}
                  onChange={handleChange}
                  invalid={!!fieldError("code")}
                />
                <FormFeedback>{fieldError("code")}</FormFeedback>
              </Col>
              <Col md="6" className="mb-3">
                <Label>Price <span className="text-danger">*</span></Label>
                <Input
                  name="price"
                  type="text"
                  inputMode="numeric"
                  placeholder="Price"
                  value={form.price}
                  onChange={handleChange}
                  invalid={!!fieldError("price")}
                />
                <FormFeedback>{fieldError("price")}</FormFeedback>
              </Col>
              <Col md="6" className="mb-3">
                <Label>Display Price</Label>
                <Input
                  name="displayPrice"
                  type="text"
                  inputMode="numeric"
                  placeholder="Display price"
                  value={form.displayPrice}
                  onChange={handleChange}
                  invalid={!!fieldError("displayPrice")}
                />
                <FormFeedback>{fieldError("displayPrice")}</FormFeedback>
              </Col>
              <Col md="6" className="mb-3">
                <Label>Validity (months) <span className="text-danger">*</span></Label>
                <Input
                  name="termValidity"
                  type="text"
                  inputMode="numeric"
                  placeholder="e.g. 12"
                  value={form.termValidity}
                  onChange={handleChange}
                  invalid={!!fieldError("termValidity")}
                />
                <FormFeedback>{fieldError("termValidity")}</FormFeedback>
              </Col>
              <Col md="6" className="mb-3">
                <Label>Total Turns <span className="text-danger">*</span></Label>
                <Input
                  name="turns"
                  type="text"
                  inputMode="numeric"
                  placeholder="e.g. 15"
                  value={form.turns}
                  onChange={handleChange}
                  invalid={!!fieldError("turns")}
                />
                <FormFeedback>{fieldError("turns")}</FormFeedback>
              </Col>
              <Col md="6" className="mb-3">
                <Label>Order Range From <span className="text-danger">*</span></Label>
                <Input
                  name="priceFrom"
                  type="text"
                  inputMode="numeric"
                  placeholder="0"
                  value={form.priceFrom}
                  onChange={handleChange}
                  invalid={!!fieldError("priceFrom")}
                />
                <FormFeedback>{fieldError("priceFrom")}</FormFeedback>
              </Col>
              <Col md="6" className="mb-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <Label className="mb-0">
                    Order Range To{" "}
                    {!form.noLimit && <span className="text-danger">*</span>}
                  </Label>
                  <div className="form-check form-switch mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="packageNoLimit"
                      checked={form.noLimit}
                      onChange={e =>
                        setForm(prev => ({
                          ...prev,
                          noLimit: e.target.checked,
                          priceTo: e.target.checked ? "" : prev.priceTo,
                        }))
                      }
                    />
                    <label className="form-check-label" htmlFor="packageNoLimit">
                      No limit
                    </label>
                  </div>
                </div>
                <Input
                  name="priceTo"
                  type="text"
                  inputMode="numeric"
                  placeholder={form.noLimit ? "No limit" : "0"}
                  value={form.noLimit ? "" : form.priceTo}
                  disabled={form.noLimit}
                  onChange={handleChange}
                  invalid={!!fieldError("priceTo")}
                />
                <FormFeedback>{fieldError("priceTo")}</FormFeedback>
              </Col>
              <Col md="12" className="mb-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <Label className="mb-0">City</Label>
                  <div className="form-check form-switch form-switch-md mb-0 d-flex align-items-center">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="packageAllCities"
                      checked={form.allCities}
                      onChange={() =>
                        setForm(prev => ({
                          ...prev,
                          allCities: !prev.allCities,
                          cities: [],
                        }))
                      }
                    />
                    <label
                      className="form-check-label font-size-14 ms-1"
                      htmlFor="packageAllCities"
                    >
                      All Cities
                    </label>
                  </div>
                </div>
                <Select
                  isMulti
                  isDisabled={form.allCities}
                  placeholder={
                    form.allCities
                      ? "Available in all cities"
                      : "Select one or more cities"
                  }
                  options={cityOptions}
                  value={cityOptions.filter(o => form.cities.includes(o.value))}
                  onChange={selected =>
                    setForm(prev => ({
                      ...prev,
                      cities: (selected || []).map(o => o.value),
                    }))
                  }
                  classNamePrefix="package-city"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={{
                    menuPortal: base => ({ ...base, zIndex: 1060 }),
                    menu: base => ({ ...base, backgroundColor: "#fff" }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isFocused ? "#556ee6" : "#fff",
                      color: state.isFocused ? "#fff" : "#495057",
                    }),
                  }}
                />
                {fieldError("cities") && (
                  <div className="invalid-feedback d-block">
                    {fieldError("cities")}
                  </div>
                )}
              </Col>
            </Row>
            <div className="mb-3">
              <Label>Description</Label>
              <Input
                type="textarea"
                name="description"
                rows="3"
                placeholder="Describe what this package includes"
                value={form.description}
                onChange={handleChange}
              />
            </div>
            <Row>
            <Col md="6" className="mb-3">
              <Label>Image</Label>
              <div className="d-flex align-items-center gap-3">
                <PackageImage src={form.image} size={72} />
                <div>
                  <Label
                    htmlFor="packageImage"
                    className={`btn btn-light btn-sm mb-1 ${uploading ? "disabled" : ""}`}
                  >
                    <i className={`bx ${uploading ? "bx-loader-alt bx-spin" : "bx-upload"} me-1`} />
                    {uploading
                      ? "Uploading..."
                      : form.image
                        ? "Change Image"
                        : "Upload Image"}
                  </Label>
                  {form.image && (
                    <Button
                      color="link"
                      size="sm"
                      className="text-danger mb-1"
                      onClick={() => setForm(prev => ({ ...prev, image: "" }))}
                    >
                      Remove
                    </Button>
                  )}
                  <input
                    id="packageImage"
                    type="file"
                    accept="image/png,image/jpeg"
                    className="d-none"
                    disabled={uploading}
                    onChange={handleImageChange}
                  />
                  <div className="text-muted font-size-12">
                    JPG or PNG, max 1 MB
                  </div>
                </div>
              </div>
            </Col>
            <Col md="6" className="mb-3">
              <Label htmlFor="packageStatus">Status</Label>
              <div className="form-check form-switch form-switch-md mb-0 mt-1">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="packageStatus"
                  checked={form.status}
                  onChange={() =>
                    setForm(prev => ({ ...prev, status: !prev.status }))
                  }
                />
                <label className="form-check-label" htmlFor="packageStatus">
                  {form.status ? "Enabled" : "Disabled"}
                </label>
              </div>
            </Col>
            </Row>
            <div className="text-end">
              <Button
                color="secondary"
                type="button"
                className="me-2"
                onClick={closeModal}
              >
                Close
              </Button>
              <Button
                color="success"
                type="submit"
                disabled={saving || uploading}
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
      <ToastContainer />
    </React.Fragment>
  )
}

export default Package
